import { useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { GenericTable } from "../../../Shared/components/Table/GenericTable";
import { TableActionButtons } from "../../../Shared/components/Table/TableActionButtons";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { useNavigate } from "react-router-dom";
import { ApprovalService } from "../services/approval.service";
import { IApproval } from "../models/Approval";
import { ConfirmDialog } from "../../../Shared/components/ConfirmDialog";
import { useTranslation } from "react-i18next";

export const Approvals = () => {
  const {t} = useTranslation()
  const navigate = useNavigate();
  const message =t('loading_message')
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedApprovals, setSelectedApprovals] = useState<IApproval[]>([]);
  const [selectedApproval, setSelectedApproval] = useState<IApproval | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const findApprovals = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: globalFilter,
      };
      setIsLoading(true);
      const response = await ApprovalService.getAll(pageOptions);
      setIsLoading(false);
      return response;
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: [
      "vehicle",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: findApprovals,
    placeholderData: (previous) => previous?.data,
  });

  const debouncedSetFilter = useMemo(
    () =>
      debounce((val: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGlobalFilter(val);
      }, 200),
    []
  );

  const onPaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  const getPageCount = () => {
    const total = query.data?.meta?.itemCount ?? 0;
    return Math.ceil(total / pagination.pageSize);
  };

  const onRowViewAction = (row: IApproval) => {
    setSelectedApproval(row);
    navigate(`/services/accounts/approvals/details/?approval=${row.id}`);
  };

  const onRowDeleteAction = (row: IApproval) => {
    setSelectedApproval(row);
    setIsDialogOpen(true);
  };

  const deleteAction = async () => {
    const approvals =
      selectedApprovals?.length > 0
        ? selectedApprovals.map((x) => x.id!)
        : [selectedApproval?.id ?? ""];
    try {
      const response = await ApprovalService.delete(approvals);
      if (response) {
        query.refetch();
      }
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    }
  };

  const onExportAction = () => {
    UtilService.exportToExcel(query?.data?.data, t('approval'));
  };

  const columns = useMemo<ColumnDef<IApproval>[]>(
    () => [
      {
        accessorKey: "contractNumber",
        header: t('contract_number'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "authorizedRoute",
        header: t('authorized_route'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "validFrom",
        header: t('valid_from'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "validUntil",
        header: t('valid_until'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "isAuthorized",
        header: t('authorization'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "requestedAt",
        header: t('date_requested'),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <div className="">
      <ConfirmDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={deleteAction}
        itemName={`${t('approval_form')} ${
          selectedApproval?.company?.name ?? '"Approval"'
        }`}
      />
      <div className="relative">
        <GenericTable
          isLoading={isLoading}
          data={query?.data?.data ?? []}
          meta={query?.data?.meta ?? 0}
          columns={columns}
          globalFilter={globalFilter}
          onGlobalFilterChange={debouncedSetFilter}
          pageCount={getPageCount()}
          onPaginationChange={onPaginationChange}
          onRowSelectionChange={setSelectedApprovals}
          onMore={onRowViewAction}
          onDelete={onRowDeleteAction}
          actionButtons={<TableActionButtons onExport={onExportAction} />}
        />
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </div>
  );
};
